import { push } from 'react-router-redux';
import type {
	GetEditCustomerPageResult,
	SaveCustomerAttributesArgs,
	SaveCustomerAttributesResult,
} from '@ogm3/server-types';
import type { RemoteData } from '../async-actions';
import { createFetchAction } from '../async-actions';
import { toastr } from '../helpers/Toastr';
import {
	GetEditCustomerPage,
	SaveCustomerAttributes,
} from '../remote/hubs/Web_Controllers_V3_SyscoSelfService_ManageCustomersController';

// ======
// State
// ======

export interface EditCustomerViewModel {
	customerName: string;
	nationalId: string;
	specIndicator: boolean;
	autoPhaseOut: boolean;
	syncPropItems: boolean;
	dailyRefresh: boolean;
	isSaving: boolean;
	hasOgmAi: boolean;
	hasProductTagging: boolean;
}

export interface EditCustomerState {
	viewModel: RemoteData<EditCustomerViewModel, string[]>;
	customerId: number;
}

export type ToggleField =
	| 'specIndicator'
	| 'autoPhaseOut'
	| 'syncPropItems'
	| 'dailyRefresh';

export function initialState(): EditCustomerState {
	return {
		viewModel: { type: 'initial' },
		customerId: -1,
	};
}

export function EditCustomerReducer(
	state: EditCustomerState = initialState(),
	action: EditCustomerAction
): EditCustomerState {
	switch (action.type) {
		case '@customers:EDIT_CUSTOMER_PAGE_LOADED_PENDING': {
			const customerId = action.payload;
			return {
				...initialState(),
				customerId,
			};
		}
		case '@customers:EDIT_CUSTOMER_PAGE_LOADED_SUCCESS': {
			return {
				...state,
				viewModel: editCustomerResultToViewModel(action.payload),
				customerId: action.payload.customerId,
			};
		}
	}

	if (state.viewModel.type !== 'success') {
		return state;
	}

	switch (action.type) {
		case '@customers:TOGGLE_CHANGED': {
			const nextData = { ...state.viewModel.data };
			nextData[action.payload] = !nextData[action.payload];

			return {
				...state,
				viewModel: {
					...state.viewModel,
					data: nextData,
				},
			};
		}
		case '@customers:CUSTOMER_ATTRIBUTES_SAVED_PENDING': {
			const nextData = { ...state.viewModel.data };
			nextData.isSaving = true;

			return {
				...state,
				viewModel: {
					...state.viewModel,
					data: nextData,
				},
			};
		}
		case '@customers:CUSTOMER_ATTRIBUTES_SAVED_FAILURE': {
			const nextData = { ...state.viewModel.data };
			nextData.isSaving = false;

			return {
				...state,
				viewModel: {
					...state.viewModel,
					data: nextData,
				},
			};
		}
		case '@customers:CUSTOMER_ATTRIBUTES_SAVED_SUCCESS': {
			const nextData = { ...state.viewModel.data };
			nextData.isSaving = false;

			return {
				...state,
				viewModel: {
					...state.viewModel,
					data: nextData,
				},
			};
		}
	}

	return state;
}

function editCustomerResultToViewModel(
	args: GetEditCustomerPageResult
): EditCustomerState['viewModel'] {
	return {
		type: 'success',
		data: {
			customerName: args.customerName,
			nationalId: args.nationalId,
			isSaving: false,
			autoPhaseOut: args.hasAutoPhaseOut,
			dailyRefresh: args.hasDailyRefresh,
			specIndicator: args.hasSpecIndicator,
			syncPropItems: args.hasSyncPropItems,
			hasOgmAi: args.hasOgmAi,
			hasProductTagging: args.hasProductTagging,
		},
	};
}

// ========
// Actions
// ========

export type EditCustomerAction =
	| EDIT_CUSTOMER_PAGE_LOADED_PENDING
	| EDIT_CUSTOMER_PAGE_LOADED_FAILURE
	| EDIT_CUSTOMER_PAGE_LOADED_SUCCESS
	| TOGGLE_CHANGED
	| CUSTOMER_ATTRIBUTES_SAVED_PENDING
	| CUSTOMER_ATTRIBUTES_SAVED_FAILURE
	| CUSTOMER_ATTRIBUTES_SAVED_SUCCESS;

export interface EDIT_CUSTOMER_PAGE_LOADED_PENDING {
	type: '@customers:EDIT_CUSTOMER_PAGE_LOADED_PENDING';
	payload: number;
}
export interface EDIT_CUSTOMER_PAGE_LOADED_FAILURE {
	type: '@customers:EDIT_CUSTOMER_PAGE_LOADED_FAILURE';
	errors: string[];
}
export interface EDIT_CUSTOMER_PAGE_LOADED_SUCCESS {
	type: '@customers:EDIT_CUSTOMER_PAGE_LOADED_SUCCESS';
	payload: GetEditCustomerPageResult;
}

export interface EditCustomerPageLoadedArgs {
	customerId: number;
}

export const editCustomerPageLoaded = createFetchAction<
	EditCustomerPageLoadedArgs,
	EDIT_CUSTOMER_PAGE_LOADED_PENDING,
	EDIT_CUSTOMER_PAGE_LOADED_FAILURE,
	EDIT_CUSTOMER_PAGE_LOADED_SUCCESS
>(
	'@customers:EDIT_CUSTOMER_PAGE_LOADED_PENDING',
	'@customers:EDIT_CUSTOMER_PAGE_LOADED_FAILURE',
	'@customers:EDIT_CUSTOMER_PAGE_LOADED_SUCCESS',
	(args) => GetEditCustomerPage(args.customerId),
	(args) => args.customerId
);

export interface TOGGLE_CHANGED {
	type: '@customers:TOGGLE_CHANGED';
	payload: ToggleField;
}

export function toggleChanged(field: ToggleField): TOGGLE_CHANGED {
	return {
		type: '@customers:TOGGLE_CHANGED',
		payload: field,
	};
}

export function navigateToCustomers() {
	return push('/customers');
}

export interface CUSTOMER_ATTRIBUTES_SAVED_PENDING {
	type: '@customers:CUSTOMER_ATTRIBUTES_SAVED_PENDING';
}
export interface CUSTOMER_ATTRIBUTES_SAVED_FAILURE {
	type: '@customers:CUSTOMER_ATTRIBUTES_SAVED_FAILURE';
	errors: string[];
}
export interface CUSTOMER_ATTRIBUTES_SAVED_SUCCESS {
	type: '@customers:CUSTOMER_ATTRIBUTES_SAVED_SUCCESS';
	payload: SaveCustomerAttributesResult;
}

export const customerAttributesSaved = createFetchAction<
	{},
	CUSTOMER_ATTRIBUTES_SAVED_PENDING,
	CUSTOMER_ATTRIBUTES_SAVED_FAILURE,
	CUSTOMER_ATTRIBUTES_SAVED_SUCCESS
>(
	'@customers:CUSTOMER_ATTRIBUTES_SAVED_PENDING',
	'@customers:CUSTOMER_ATTRIBUTES_SAVED_FAILURE',
	'@customers:CUSTOMER_ATTRIBUTES_SAVED_SUCCESS',
	(args, dispatch, getState) => {
		const { editCustomer } = getState();
		const vm = editCustomer.viewModel;

		if (vm.type !== 'success') {
			throw new Error('page not loaded');
		}

		const apiArgs: SaveCustomerAttributesArgs = {
			customerId: editCustomer.customerId,
			hasAutoPhaseOut: vm.data.autoPhaseOut,
			hasDailyRefresh: vm.data.dailyRefresh,
			hasSpecIndicator: vm.data.specIndicator,
			hasSyncPropItems: vm.data.syncPropItems,
		};

		return SaveCustomerAttributes(apiArgs).then((res) => {
			if (res.kind === 'success') {
				toastr.success({ title: 'Success', body: 'Customer attributes saved' });
			}

			if (res.kind === 'failure') {
				toastr.error({
					title: 'Could not save attributes',
					body: res.errors.join(', '),
				});
			}

			return res;
		});
	}
);
